import { HEX_SIZE } from './HexMetrics';

/**
 * Axial coordinates for a flat-top hex grid, plus the conversions we actually
 * need. Cube coordinates are derived on demand for the algorithms that want
 * them (distance, rotation, line drawing).
 *
 * Reference: https://www.redblobgames.com/grids/hexagons/
 */

export interface Axial {
  q: number;
  r: number;
}

export interface Cube {
  x: number;
  y: number;
  z: number;
}

/**
 * Neighbour offsets ordered to match HexMetrics.CORNERS: edge i sits between
 * CORNERS[i] and CORNERS[i + 1], so DIRECTIONS[i] is the neighbour across it.
 * Directions 3..5 are the negatives of 0..2, which is why HexChunk only builds
 * bridges for the first three — each shared edge is then emitted exactly once.
 */
export const DIRECTIONS: readonly Axial[] = [
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
  { q: 1, r: 0 },
];

export function neighbor(cell: Axial, direction: number): Axial {
  const offset = DIRECTIONS[direction % 6];
  return { q: cell.q + offset.q, r: cell.r + offset.r };
}

/** Flat-top axial to world position on the XZ plane. */
export function axialToWorld(q: number, r: number): { x: number; z: number } {
  return {
    x: HEX_SIZE * 1.5 * q,
    z: HEX_SIZE * Math.sqrt(3) * (r + q / 2),
  };
}

export function axialToCube(q: number, r: number): Cube {
  return { x: q, y: -q - r, z: r };
}

/**
 * Inverse of axialToWorld. Returns fractional coordinates — pass them through
 * roundAxial to land on a cell.
 */
export function worldToAxial(x: number, z: number): { q: number; r: number } {
  const q = x / (1.5 * HEX_SIZE);
  return { q, r: z / (Math.sqrt(3) * HEX_SIZE) - q / 2 };
}

/**
 * Nearest cell to a fractional coordinate.
 *
 * Rounding q and r separately would pick the wrong cell near the corners, where
 * the hexagon and the axial square disagree. Rounding in cube space and then
 * repairing whichever component moved furthest keeps x + y + z = 0, which is
 * what makes the result the genuinely nearest hexagon.
 */
export function roundAxial(q: number, r: number): Axial {
  const y = -q - r;

  let roundedQ = Math.round(q);
  let roundedY = Math.round(y);
  let roundedR = Math.round(r);

  const driftQ = Math.abs(roundedQ - q);
  const driftY = Math.abs(roundedY - y);
  const driftR = Math.abs(roundedR - r);

  if (driftQ > driftY && driftQ > driftR) {
    roundedQ = -roundedY - roundedR;
  } else if (driftY > driftR) {
    roundedY = -roundedQ - roundedR;
  } else {
    roundedR = -roundedQ - roundedY;
  }

  return { q: roundedQ, r: roundedR };
}

export function distance(a: Axial, b: Axial): number {
  const ca = axialToCube(a.q, a.r);
  const cb = axialToCube(b.q, b.r);
  return Math.max(
    Math.abs(ca.x - cb.x),
    Math.abs(ca.y - cb.y),
    Math.abs(ca.z - cb.z)
  );
}

/**
 * Every cell within `radius` of the origin — a hexagon of hexes, centred on
 * (0, 0), holding 3N² + 3N + 1 cells.
 *
 * The bounds come from the cube constraint x + y + z = 0: for a given q, the
 * valid r runs between -radius and radius, clipped so that |q + r| never
 * exceeds the radius either.
 */
export function hexagonalArea(radius: number): Axial[] {
  const cells: Axial[] = [];

  for (let q = -radius; q <= radius; q++) {
    const from = Math.max(-radius, -q - radius);
    const to = Math.min(radius, -q + radius);
    for (let r = from; r <= to; r++) {
      cells.push({ q, r });
    }
  }

  return cells;
}

/**
 * Packs a pair of small signed integers into one number, for use as a Map key.
 *
 * Numeric keys rather than strings: at stress-test scale the grid produces
 * millions of lookups, and building a string for each one dominated everything
 * else. The stride is wide enough for coordinates well past ±32000, and the
 * result stays far inside Number.MAX_SAFE_INTEGER.
 */
const KEY_OFFSET = 32768;
const KEY_STRIDE = 65536;

export function packKey(a: number, b: number): number {
  return (a + KEY_OFFSET) * KEY_STRIDE + (b + KEY_OFFSET);
}

export function axialKey(q: number, r: number): number {
  return packKey(q, r);
}
